import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router-dom";
import { AiOutlineMail, AiOutlineLock, AiOutlineUser, AiOutlinePhone, AiOutlineHome, AiOutlineIdcard, AiOutlineBank } from "react-icons/ai";

export default function AddNewUser() {
  const { t } = useTranslation();
  const generatePassword = () => {
    return Math.random().toString(36).slice(-8);
  };
  const location = useLocation();

  const [formData, setFormData] = useState({
    email: "",
    password: generatePassword(),
    firstName: "",
    lastName: "",
    phone: "",
    index: "",
    street: "",
    house_number: "",
    city: "",
    pesel: "",
    account_number: ""
  });
  const [showFullInfo, setShowFullInfo] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const firstName = queryParams.get("first_name");
    const lastName = queryParams.get("last_name");
    setFormData((prevData) => ({
      ...prevData,
      firstName: firstName || prevData.firstName,
      lastName: lastName || prevData.lastName,
    }));
  }, [location.search]);

  useEffect(() => {
    setFormData((prevData) => ({ ...prevData, password: generatePassword() }));
  }, []);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const latinOnlyRegex = /^[A-Za-z]*$/;

  const validateField = (name, value) => {
    const val = value != null ? String(value).trim() : "";
    switch (name) {
      case "email":
        if (!val) return t("emailWarning");
        return emailRegex.test(value) ? null : t("emailWarning");
      case "firstName":
        if (!val) return t("nameWarning");
        return latinOnlyRegex.test(value) ? null : t("nameWarning");
      case "lastName":
        if (!val) return t("surnameWarning");
        return latinOnlyRegex.test(value) ? null : t("surnameWarning");
      case "phone":
        if (!val) return null;
        return /^\+48\d{9}$/.test(value) ? null : t("phoneWarning");
      case "pesel":
        if (!val) return null;
        return /^\d{11}$/.test(value) ? null : t("peselWarning");
      default:
        return null;
    }
  };

  const validate = () => {
    const errs = {};
    ["email", "password", "firstName", "lastName"].forEach((key) => {
      const msg = key === "password"
        ? (formData.password && formData.password.trim() ? null : t("password"))
        : validateField(key, formData[key]);
      if (msg) errs[key] = msg;
    });
    const phoneMsg = validateField("phone", formData.phone);
    if (phoneMsg) errs.phone = phoneMsg;
    const peselMsg = validateField("pesel", formData.pesel);
    if (peselMsg) errs.pesel = peselMsg;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Латиниця та дозволені в email символи: 0-9, @ . _ + -
  const emailAllowedRegex = /[^A-Za-z0-9@._+-]/g;

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "firstName" || name === "lastName") {
      newValue = value.replace(/[^A-Za-z]/g, "");
    } else if (name === "email") {
      newValue = value.replace(emailAllowedRegex, "");
    }
    const next = { ...formData, [name]: newValue };
    setFormData(next);
    const msg = validateField(name, newValue);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[name] = msg; else delete next[name];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!validate()) return;

    try {
      const optionalKeys = ["phone", "index", "street", "house_number", "city", "pesel", "account_number"];
      const payload = { email: formData.email, password: formData.password, firstName: formData.firstName, lastName: formData.lastName, role: "new" };
      optionalKeys.forEach((key) => {
        const v = formData[key];
        if (v != null && String(v).trim() !== "") payload[key] = formData[key];
      });
      const response = await fetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok) {
        const loginUrl = "https://godrive.com.pl/login";
        const userInfo = `${t("userCreated")}\n${t("email")} ${formData.email}\n${t("password")} ${formData.password}\n${t("loginPage")} ${loginUrl}`;
        navigator.clipboard.writeText(userInfo);
        alert(t("clipboardMessage"));
        window.location.href = "/admin/users";
      } else {
        setMessage(data.error || t("newUserError"));
      }
    } catch (error) {
      setMessage(t("servConError"));
    }
  };

  return (
    <div className="container mt-5">
      <div className="mx-auto" style={{ maxWidth: showFullInfo ? "900px" : "500px" }}>
        <h2 className="text-center">{t("addNewUser")}</h2>
        <form onSubmit={handleSubmit}>
          {!showFullInfo ? (
            <>
              <div className="mb-3">
                <label htmlFor="email" className="form-label d-flex align-items-center gap-2">
                  <AiOutlineMail size={18} /> {t("email")} <span className="text-danger">*</span>
                </label>
                <input type="email" className={`form-control ${errors.email ? "is-invalid" : ""}`} name="email" value={formData.email} onChange={handleChange} placeholder="name@example.com" required />
                {errors.email && <div className="text-danger small">{errors.email}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="password" className="form-label d-flex align-items-center gap-2">
                  <AiOutlineLock size={18} /> {t("password")} <span className="text-danger">*</span>
                </label>
                <input type="text" className="form-control" name="password" value={formData.password} disabled />
                {errors.password && <div className="text-danger small">{errors.password}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="firstName" className="form-label d-flex align-items-center gap-2">
                  <AiOutlineUser size={18} /> {t("name")} <span className="text-danger">*</span>
                </label>
                <input type="text" className={`form-control ${errors.firstName ? "is-invalid" : ""}`} name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" required />
                {errors.firstName && <div className="text-danger small">{errors.firstName}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="lastName" className="form-label d-flex align-items-center gap-2">
                  <AiOutlineUser size={18} /> {t("surname")} <span className="text-danger">*</span>
                </label>
                <input type="text" className={`form-control ${errors.lastName ? "is-invalid" : ""}`} name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" required />
                {errors.lastName && <div className="text-danger small">{errors.lastName}</div>}
              </div>
            </>
          ) : (
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="email" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineMail size={18} /> {t("email")} <span className="text-danger">*</span>
                  </label>
                  <input type="email" className={`form-control ${errors.email ? "is-invalid" : ""}`} name="email" value={formData.email} onChange={handleChange} placeholder="name@example.com" required />
                  {errors.email && <div className="text-danger small">{errors.email}</div>}
                </div>
                <div className="mb-3">
                  <label htmlFor="password" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineLock size={18} /> {t("password")} <span className="text-danger">*</span>
                  </label>
                  <input type="text" className="form-control" name="password" value={formData.password} disabled />
                  {errors.password && <div className="text-danger small">{errors.password}</div>}
                </div>
                <div className="mb-3">
                  <label htmlFor="firstName" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineUser size={18} /> {t("name")} <span className="text-danger">*</span>
                  </label>
                  <input type="text" className={`form-control ${errors.firstName ? "is-invalid" : ""}`} name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" required />
                  {errors.firstName && <div className="text-danger small">{errors.firstName}</div>}
                </div>
                <div className="mb-3">
                  <label htmlFor="lastName" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineUser size={18} /> {t("surname")} <span className="text-danger">*</span>
                  </label>
                  <input type="text" className={`form-control ${errors.lastName ? "is-invalid" : ""}`} name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" required />
                  {errors.lastName && <div className="text-danger small">{errors.lastName}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="phone" className="form-label d-flex align-items-center gap-2">
                    <AiOutlinePhone size={18} /> {t("phone")}
                  </label>
                  <input type="text" className={`form-control ${errors.phone ? "is-invalid" : ""}`} name="phone" value={formData.phone} onChange={handleChange} placeholder="+48XXXXXXXXX" />
                  {errors.phone && <div className="text-danger small">{errors.phone}</div>}
                </div>
                <div className="mb-3">
                  <label htmlFor="index" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineHome size={18} /> {t("index")}
                  </label>
                  <input type="text" className="form-control" name="index" value={formData.index} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="street" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineHome size={18} /> {t("street")}
                  </label>
                  <input type="text" className="form-control" name="street" value={formData.street} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="house_number" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineHome size={18} /> {t("houseNumber")}
                  </label>
                  <input type="text" className="form-control" name="house_number" value={formData.house_number} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="city" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineHome size={18} /> {t("city")}
                  </label>
                  <input type="text" className="form-control" name="city" value={formData.city} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label htmlFor="pesel" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineIdcard size={18} /> {t("pesel")}
                  </label>
                  <input type="text" className={`form-control ${errors.pesel ? "is-invalid" : ""}`} name="pesel" value={formData.pesel} onChange={handleChange} />
                  {errors.pesel && <div className="text-danger small">{errors.pesel}</div>}
                </div>
                <div className="mb-3">
                  <label htmlFor="account_number" className="form-label d-flex align-items-center gap-2">
                    <AiOutlineBank size={18} /> {t("accountNumber")}
                  </label>
                  <input type="text" className="form-control" name="account_number" value={formData.account_number} onChange={handleChange} />
                </div>
              </div>
            </div>
          )}

          <div className="mb-3 form-check">
            <input type="checkbox" className="form-check-input" id="showFullInfo" checked={showFullInfo} onChange={(e) => setShowFullInfo(e.target.checked)} />
            <label className="form-check-label" htmlFor="showFullInfo">{t("addFullInfo")}</label>
          </div>

          <button type="submit" className="btn btn-primary w-100">{t("add")}</button>
        </form>
        {message && <p className="text-center text-danger mt-3">{message}</p>}
      </div>
    </div>
  );
}
